/**
 * api/analysis.js — entregáveis do modo Análise.
 * POST /executions/{id}/analysis/recommendations → gera o .md de recomendações.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, ApiError } from './client';

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * pollAnalysisJob — acompanha um entregável ASSÍNCRONO da Análise até concluir.
 *
 * O backend gera o Relatório em background (evita o timeout de proxies reversos —
 * Cloudflare ~100s → HTTP 524/525 — numa geração de minutos) e expõe o status via
 * GET .../analysis/jobs/{jobId}. Aqui fazemos polling:
 *   - 'done'    → devolve o result (o Output é recarregado pelo onSuccess);
 *   - 'failed'  → lança ApiError (a UI mostra a mensagem);
 *   - 'unknown' → a API reiniciou; o artefato, se pronto, já está persistido —
 *                 devolvemos {status:'unknown'} e o Output é recarregado.
 * Mantido dentro do mutationFn de propósito: assim `isPending` cobre toda a geração.
 */
async function pollAnalysisJob(api, executionId, jobId, { intervalMs = 3000, maxMs = 10 * 60 * 1000 } = {}) {
  const started = Date.now();
  // pequeno atraso inicial: a geração leva minutos, não faz sentido perguntar já.
  await _sleep(intervalMs);
  for (;;) {
    const s = await api.get(`/executions/${executionId}/analysis/jobs/${jobId}`);
    if (s?.status === 'done') return s.result ?? s;
    if (s?.status === 'unknown') return s;
    if (s?.status === 'failed') {
      throw new ApiError(502, 'Falha ao gerar o relatório', s?.error || '');
    }
    if (Date.now() - started > maxMs) {
      throw new ApiError(504, 'A geração demorou demais', 'Tempo limite de espera excedido — tente recarregar o Output.');
    }
    await _sleep(intervalMs);
  }
}

/**
 * useRemediateAnalysis — UMA rodada de remediação da análise (semi-auto, aprovada
 * pelo usuário): o analista gera o conteúdo das lacunas apontadas pela Conferência
 * e anexa ao Relatório. O chamador reconfere a entrega em seguida (loop até 100%).
 */
export function useRemediateAnalysis(executionId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    meta: {
      aiLabel: 'A IA está corrigindo as lacunas da análise…',
      aiSublabel: 'Gerando o conteúdo que faltou (e a metodologia dos NFRs) para cobrir 100% do solicitado.',
    },
    // Assíncrono (202 + jobId + polling). O resultado do job traz gapsAddressed/
    // gapsTotal/appended — o chamador reconfere a entrega em seguida.
    mutationFn: async () => {
      const started = await api.post(`/executions/${executionId}/analysis/remediate`, {});
      const jobId = started?.jobId;
      if (!jobId) return started; // retrocompat com backend síncrono antigo
      return pollAnalysisJob(api, executionId, jobId);
    },
  });
}

export function useGenerateRecommendations(executionId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: {
      aiLabel: 'A IA está gerando as recomendações & mitigações…',
      aiSublabel: 'Consolidando os achados da análise em um documento acionável (escopo de projeto futuro).',
    },
    // Assíncrono (202 + jobId + polling); o result do job traz {content}.
    mutationFn: async () => {
      const started = await api.post(`/executions/${executionId}/analysis/recommendations`, {});
      const jobId = started?.jobId;
      if (!jobId) return started; // retrocompat com backend síncrono antigo
      return pollAnalysisJob(api, executionId, jobId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['executions', executionId, 'output'] }),
  });
}

/**
 * useGenerateReport — (re)gera o RELATÓRIO DE ANÁLISE completo (resultados de todas
 * as áreas), consolidando enquadramento + requisitos + o código indexado do repo.
 */
export function useGenerateReport(executionId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: {
      aiLabel: 'A IA está consolidando o relatório de análise completo…',
      aiSublabel: 'Entregando os resultados de todas as áreas com evidências do repositório.',
    },
    // Assíncrono: dispara (202 + jobId) e faz polling até concluir. `isPending`
    // cobre toda a geração, então o botão segue em "Consolidando…" sem que a
    // requisição fique pendurada (o que estourava o timeout do Cloudflare = 525).
    mutationFn: async () => {
      const started = await api.post(`/executions/${executionId}/analysis/report`, {});
      const jobId = started?.jobId;
      if (!jobId) return started; // retrocompat: backend antigo devolvia o conteúdo direto
      return pollAnalysisJob(api, executionId, jobId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['executions', executionId, 'output'] }),
  });
}
